import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FocalImage } from "@/components/ui/focal-image";
import { PlaceHolderImages } from "@/lib/placeholder-images";
import { toggleRecipeFavorite } from "@/lib/recipe-service";
import type { Recipe, RecipeBox } from "@/lib/types";
import { Loader2, Star } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

interface RecipeCardProps {
    recipe: Recipe;
    userId?: string | null;
    recipeBox?: RecipeBox | null;
    onFavoriteToggle?: (isFavorite: boolean) => void;
}



const FALLBACK_RECIPE_IMAGE = PlaceHolderImages[0]?.imageUrl ?? "https://picsum.photos/seed/placeholder/600/400";
const FALLBACK_IMAGE_HINT = PlaceHolderImages[0]?.imageHint ?? "recipe placeholder";

export function RecipeCard({ recipe, userId, recipeBox, onFavoriteToggle }: Readonly<RecipeCardProps>) {
    const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
    const [optimisticFavorite, setOptimisticFavorite] = useState<boolean | null>(null);
    const imageUrl = recipe.imageUrl || FALLBACK_RECIPE_IMAGE;
    const imageHint = recipe.imageHint || FALLBACK_IMAGE_HINT;

    // Use optimistic state if available, otherwise fall back to prop
    // The optimistic state will be cleared after the async operation completes,
    // allowing Firestore subscription updates to take over
    const isFavorite = optimisticFavorite !== null ? optimisticFavorite : (recipe.isFavorite ?? false);

    const handleToggleFavorite = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!userId || isTogglingFavorite) {
            return;
        }

        // Optimistic update - update UI immediately
        const newFavoriteStatus = !isFavorite;
        setOptimisticFavorite(newFavoriteStatus);
        setIsTogglingFavorite(true);

        try {
            const confirmedFavoriteStatus = await toggleRecipeFavorite(recipe.id, userId, isFavorite, recipeBox);
            // Clear optimistic state - Firestore subscription will update the prop
            setOptimisticFavorite(null);
            onFavoriteToggle?.(confirmedFavoriteStatus);
        } catch (error) {
            console.error("Failed to toggle favorite", error);
            // Revert optimistic update on error - show original state
            setOptimisticFavorite(null);
        } finally {
            setIsTogglingFavorite(false);
        }
    };

    return (
        <Link to={`/recipe/${recipe.id}`} className="group block">
            <div className="flex flex-col gap-2">
                {/* Image Card - iOS app icon style */}
                <div className="relative aspect-square w-full overflow-hidden rounded-2xl shadow-md transition-all duration-200 ease-in-out hover:shadow-xl hover:-translate-y-1">
                    {userId && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="absolute left-2 top-2 z-10 h-8 w-8 rounded-full bg-background/80 backdrop-blur transition-opacity duration-200 hover:bg-background"
                            onClick={handleToggleFavorite}
                            disabled={isTogglingFavorite}
                            aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
                        >
                            {isTogglingFavorite ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Star className={`h-4 w-4 ${isFavorite ? "fill-yellow-500 text-yellow-500" : ""}`} />
                            )}
                        </Button>
                    )}
                    {recipe.isDraft && (
                        <Badge 
                            variant="secondary" 
                            className="absolute right-2 top-2 z-10 bg-background/80 backdrop-blur text-[10px] px-2 py-0.5"
                        >
                            DRAFT
                        </Badge>
                    )}
                    <FocalImage
                        src={imageUrl}
                        alt={recipe.title}
                        className="h-full w-full"
                        data-ai-hint={imageHint}
                        focalPointX={recipe.imageFocalPointX}
                        focalPointY={recipe.imageFocalPointY}
                        zoom={recipe.imageZoom}
                    />
                </div>
                
                {/* Title centered below - iOS app icon style */}
                <div className="text-center">
                    <h3 className="font-headline text-sm leading-tight line-clamp-2">
                        {recipe.title}
                    </h3>
                </div>
            </div>
        </Link>
    );
}
